// Admin utilities and capability hooks.
import { useIsLive, useModuleGlobal, useMyRoles } from "util/datastore";
import { useFirebaseUser } from "internal/firebase";
import { Banner } from "./banner";
import { UtilityText } from "./text";
import { ConversationScreen } from "./screen";
import { roles } from "feature/feature-roles";
import { getDeploymentConfig } from "util/util";
import { defineModuleGlobal, defineModuleMap } from "util/moduledata";
import { t } from "util/stdtypes";

let global_capability_map: Record<string, any> = {};
let global_roles: Record<string, any> = roles;

const deploymentConfig = getDeploymentConfig();

export function useIsAdmin() {
    const localHostAdmin = useIsLocalhostAdmin();
    const roles = useMyRoles();
    return localHostAdmin || (roles && roles.length > 0)
}

export function useIsLocalhostAdmin() {
    const fbUser = useFirebaseUser();
    const isLive = useIsLive();
    if (!fbUser || !isLive) { return false }
    const emailDomain = fbUser.email?.split('@')?.[1];
    return deploymentConfig.adminEmailDomain && emailDomain === deploymentConfig.adminEmailDomain;
}

export function useHasCapability(capability) {
    const roles = useMyRoles();
    const localHostAdmin = useIsLocalhostAdmin();

    if (localHostAdmin) {
        return true;
    }
    if (!roles) {
        return null;
    }

    for (const role of roles) {
        const roleInfo = global_roles?.[role];
        if (roleInfo?.allCapabilities || role === 'Owner') {
            return true;
        }
        if (global_capability_map?.[role]?.[capability]) {
            return true;
        }
    }
    return false;
}

export function RestrictedScreen({capability, pad, children}) {
    const hasCapability = useHasCapability(capability);
    if (hasCapability === null) {
        return null;
    }
    if (!hasCapability) {
        return <Banner><UtilityText label={t('You do not have access to this feature')}/></Banner>
    }
    return <ConversationScreen pad={pad}>{children}</ConversationScreen>;
}


export function makeCapabilityMap(roles) {
    const capability_map = {};
    Object.keys(roles).forEach(roleKey => {
        const role = roles[roleKey];
        if (!capability_map[roleKey]) {
            capability_map[roleKey] = {};
        }
        role?.can?.forEach(capability => {
            capability_map[roleKey][capability] = true;
        });
        if (role.inherits) {
            role.inherits.forEach(inheritedRoleKey => {
                const inheritedRole = roles[inheritedRoleKey];
                inheritedRole?.can?.forEach(capability => {
                    if (!capability_map[roleKey]) {
                        capability_map[roleKey] = {};
                    }
                    capability_map[roleKey][capability] = true;
                });
            });
        }
    });
    return capability_map;
}

export function getRoles() {
    return roles;
}

export function mergeRoles(oldRoles, newRoles) {
    const mergedRoles = {...oldRoles, ...newRoles};
    Object.keys(mergedRoles).forEach(roleKey => {
        if (oldRoles[roleKey] && newRoles[roleKey]) {
            const oldRole = oldRoles[roleKey];
            const newRole = newRoles[roleKey];
            const mergedRole = {...oldRole, ...newRole};
            mergedRole.can = [
                ...oldRole.can ?? [], 
                ...newRole.can ?? []
            ]
            mergedRole.inherits = [
                ...oldRole.inherits ?? [], 
                ...newRole.inherits ?? []
            ]
            mergedRoles[roleKey] = mergedRole;
        }
    })
    return mergedRoles;
}

export function extendRoles(newRoles) {
    const mergedRoles = mergeRoles(roles, newRoles);
    global_roles = mergedRoles;
    global_capability_map = makeCapabilityMap(mergedRoles);
}


export type LegalDocLinks = {
    communityGuidelines?: string;
    privacyNotice?: string;
    termsOfService?: string;
}

const moduleGlobalSiloName = defineModuleGlobal<string>('PUBLIC', 'admin', 'siloName');
const moduleGlobalShowCollaborationNote = defineModuleGlobal<boolean>('PUBLIC', 'admin', 'showCollaborationNote');
const moduleGlobalLegalDocLinks = defineModuleGlobal<LegalDocLinks>('PUBLIC', 'admin', 'legalDocLinks');

export function useSiloName() {
    return useModuleGlobal(moduleGlobalSiloName);
}

export function useShowCollaborationNote() {
    return useModuleGlobal(moduleGlobalShowCollaborationNote);
}

export function useLegalDocLinks() {
    return useModuleGlobal(moduleGlobalLegalDocLinks);
}


// HACK (Rob): This should be part of admin, not an 'isBeta' module.
// eslint-disable-next-line module-data-owner -- FIXME: use module name 'admin'
const moduleGlobalIsBeta = defineModuleGlobal<boolean>('PUBLIC', 'isBeta', null);

export function useIsBeta() {
    return useModuleGlobal(moduleGlobalIsBeta);
}

// eslint-disable-next-line module-data-owner -- FIXME: use module name 'admin'
export const moduleGlobalLanguage = defineModuleGlobal<string>('PUBLIC', 'language', null);
